import { useMemo } from 'react';
import { CommanderAPI } from '../CommanderAPI';
import useMyWorkspacesChildren from '../../../../redux/workspace/hooks/useMyWorkspacesChildren';
import useMultipleQueries from '../../../../shared/util/rtk/useQueries';
import { DeviceStatusMapAPIResponse } from '../deviceStatus.model';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

type DeviceStatusWithWorkspaceId = DeviceStatusMapAPIResponse & {
  workspaceId: string;
};

type AggregatedResult = {
  isUninitialized: boolean;
  isFetching: boolean;
  isLoading: boolean;
  currentData?: DeviceStatusWithWorkspaceId[];
  data?: DeviceStatusWithWorkspaceId[];
  error?: unknown;
};

function useAggregatedGetDevicesConnectionStatusBySubWorkSpaces(
  enabled = true
): AggregatedResult {
  const { data: childWorkspacesDict } = useMyWorkspacesChildren({
    skip: !enabled,
  });

  const childWorkspaceIds = useMemo(
    () => (childWorkspacesDict ? Object.keys(childWorkspacesDict) : []),
    [childWorkspacesDict]
  );

  const childWorkspaceResponses = useMultipleQueries<
    DeviceStatusMapAPIResponse[] | undefined,
    string
  >(
    CommanderAPI.endpoints.getDevicesConnectionStatusByWorkspace,
    childWorkspaceIds,
    {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      refetchOnMountOrArgChange: true,
      skip: !enabled || !childWorkspaceIds.length,
    }
  );

  const aggregatedCurrentData = useMemo<
    DeviceStatusWithWorkspaceId[] | undefined
  >(() => {
    if (!enabled || !childWorkspaceIds.length) {
      return [];
    }

    const lists =
      childWorkspaceResponses.currentData ?? childWorkspaceResponses.data;
    if (!lists) {
      return undefined;
    }

    return lists.flatMap((devices, index) => {
      const workspaceId = childWorkspaceIds[index];
      return (devices ?? [])
        .filter((device) => device.mqttConnected !== true)
        .map((device) => ({
          ...device,
          workspaceId,
        }));
    });
  }, [
    childWorkspaceIds,
    childWorkspaceResponses.currentData,
    childWorkspaceResponses.data,
    enabled,
  ]);

  return useMemo(
    () => ({
      ...childWorkspaceResponses,
      currentData: aggregatedCurrentData,
      data: aggregatedCurrentData,
      isUninitialized: !enabled || childWorkspaceResponses.isUninitialized,
      isLoading:
        childWorkspaceResponses.isFetching &&
        aggregatedCurrentData === undefined,
    }),
    [aggregatedCurrentData, childWorkspaceResponses, enabled]
  );
}

export default useAggregatedGetDevicesConnectionStatusBySubWorkSpaces;
